import { default as WJElement, event, WjElementUtils } from '../wje-element/element.js';
import styles from './styles/styles.css?inline';

const MOBILE_COLLAPSED_VARIANTS = new Set(['TEXT', 'BACK', 'PARENT-TITLE', 'MENU-TITLE', 'SHEET']);

/**
 * @summary This class represents a Breadcrumb element, extending the WJElement class. It provides a navigational aid in user interfaces, displaying the current location within a hierarchy.
 * @documentation https://elements.webjet.sk/components/breadcrumb
 * @status stable
 * @augments WJElement
 * @slot - The main content of the breadcrumb.
 * @slot start - Slot for content at the start of the breadcrumb.
 * @slot end - Slot for content at the end of the breadcrumb.
 * @slot separator - Slot for a custom separator between breadcrumb items.
 * @csspart native - The native wrapper of the breadcrumb component.
 * @csspart separator - The separator between breadcrumb items.
 * @cssproperty [--wje-breadcrumb-a=var(--wje-color-contrast-8)] - The color of the breadcrumb text.
 * @cssproperty [--wje-breadcrumb-a-hover=var(--wje-color-contrast-6)] - The color of the breadcrumb separator line.
 * @cssproperty [--wje-breadcrumb-line-height=1.5] - Controls the vertical rhythm of breadcrumb text.
 * @cssproperty [--wje-breadcrumb-native-line-height=var(--wje-breadcrumb-line-height)] - Controls the line height of the native breadcrumb wrapper.
 * @cssproperty [--wje-breadcrumb-native-margin=0] - Sets outer spacing around the native breadcrumb wrapper.
 * @cssproperty [--wje-breadcrumb-native-padding=0.25rem 0.75rem] - Sets inner spacing inside the native breadcrumb wrapper.
 * @cssproperty [--wje-breadcrumb-margin=var(--wje-breadcrumb-native-margin)] - Backwards-compatible alias for native breadcrumb margin.
 * @cssproperty [--wje-breadcrumb-padding=var(--wje-breadcrumb-native-padding)] - Backwards-compatible alias for native breadcrumb padding.
 * @tag wje-breadcrumb
 */
export default class Breadcrumb extends WJElement {
    /**
     * Breadcrumb constructor method.
     */
    constructor() {
        super();

        this._showSeparator = true;
        this._showCollapsedIndicator = false;
        this.handleNativeClick = (e) => this.handleDisabledNativeClick(e);
    }

    /**
     * Sets the breadcrumb link URL.
     * @param {string|null} value Link URL.
     */
    set href(value) {
        if (value === null || value === undefined) this.removeAttribute('href');
        else this.setAttribute('href', value);
    }

    /**
     * Gets the breadcrumb link URL.
     * @returns {string|null}
     */
    get href() {
        return this.getAttribute('href');
    }

    /**
     * Sets the disabled state.
     * @param {boolean} value Disabled state.
     */
    set disabled(value) {
        if (value === true || value === '' || value === 'true' || value === 1 || value === '1') {
            this.setAttribute('disabled', '');
        } else {
            this.removeAttribute('disabled');
        }
    }

    /**
     * Gets the disabled state.
     * @returns {boolean}
     */
    get disabled() {
        return this.hasAttribute('disabled');
    }

    /**
     * Get show separator flag.
     * @returns {boolean} showSeparator - The show separator flag
     */
    get showSeparator() {
        return this._showSeparator;
    }

    /**
     * Set show separator flag.
     * @param {boolean} value The value to set
     */
    set showSeparator(value) {
        this._showSeparator = value;
    }

    /**
     * Set collapsed variant.
     * @param {string} value The value to set
     */
    set collapsedVariant(value) {
        this._collapsedVariant = value;
    }

    /**
     * Get collapsed variant.
     * @returns {string} The collapsed variant value in uppercase.
     */
    get collapsedVariant() {
        return this.collapsedVariantName.toUpperCase();
    }

    /**
     * Get collapsed variant token.
     * @returns {string}
     */
    get collapsedVariantName() {
        return String(this.parentElement?.collapsedVariant || this.parentElement?.variant || this._collapsedVariant || 'button').trim();
    }

    /**
     * Class name for the Breadcrumb element.
     * @type {string}
     */
    className = 'Breadcrumb';

    /**
     * Get CSS stylesheet for the Breadcrumb element.
     * @static
     * @returns {object} styles - The CSS styles
     */
    static get cssStyleSheet() {
        return styles;
    }

    /**
     * Get observed attributes for the Breadcrumb element.
     * @static
     * @returns {Array<string>} - The observed attributes array for the Breadcrumb element.
     */
    static get observedAttributes() {
        return ['show-collapsed-indicator', 'collapsed', 'last', 'href', 'target', 'rel', 'download', 'disabled', 'title', 'aria-label'];
    }

    /**
     * Handles attribute changes for the custom element and updates its behavior or appearance accordingly.
     * @param {string} name The name of the attribute that was changed.
     * @param {string|null} oldValue The previous value of the attribute before it was changed. Null if the attribute was not previously set.
     * @param {string|null} newValue The new value of the attribute after it was changed. Null if the attribute was removed.
     */
    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue === newValue) return;

        if (name === 'collapsed') {
            const isCollapsed = newValue !== null && WjElementUtils.stringToBoolean(newValue);

            this.classList.toggle('collapsed', isCollapsed && !this.hasAttribute('show-collapsed-indicator'));

        } else if (name === 'show-collapsed-indicator') {
            super.attributeChangedCallback?.(name, oldValue, newValue);
            const isOn = newValue !== null && WjElementUtils.stringToBoolean(newValue);
            this._showCollapsedIndicator = isOn;
            this.classList.toggle('collapsed', this.hasAttribute('collapsed') && !isOn);
            this.refresh();

        } else if (name === 'last') {
            super.attributeChangedCallback?.(name, oldValue, newValue);
            const isLast = newValue !== null && WjElementUtils.stringToBoolean(newValue);
            this.active = isLast;
            this.showSeparator = !isLast;
            this.syncAria();
            this.syncNativeAttributes();
            this.refresh();
        } else {
            this.syncAria();
            this.syncNativeAttributes();
        }
    }

    /**
     * Setup attributes for the Breadcrumb element.
     */
    setupAttributes() {
        this.isShadowRoot = 'open';
        this.syncAria();
    }

    syncAria() {
        this.setAriaState({ role: 'link' });
        if (this.active) this.setAriaState({ current: 'page' });
        else this.removeAttribute('aria-current');

        if (this.disabled) this.setAriaState({ disabled: true });
        else this.removeAttribute('aria-disabled');
    }

    /**
     * Draw method for the Breadcrumb element.
     * @returns {object} fragment - The document fragment
     */
    draw() {
        let fragment = document.createDocumentFragment();

        let native = document.createElement('a');
        native.classList.add('native-breadcrumb');
        native.setAttribute('part', 'native');

        this.native = native;
        this.syncNativeAttributes();

        let slot = document.createElement('slot');

        let start = document.createElement('slot');
        start.setAttribute('name', 'start');

        let end = document.createElement('slot');
        end.setAttribute('name', 'end');

        native.append(start, slot, end);

        // APPEND
        fragment.append(native);

        if (WjElementUtils.stringToBoolean(this._showCollapsedIndicator)) {
            // pridame button za native element
            fragment.append(this.drawCollapsedIndicator());

            // skryjeme native element
            native.classList.add('hidden');
        }

        if (this.shouldRenderSeparator()) {
            let separator = document.createElement('span');
            separator.classList.add('separator');
            separator.setAttribute('part', 'separator');

            if (WjElementUtils.hasSlot(this, 'separator')) {
                let slotSeparator = document.createElement('slot');
                slotSeparator.setAttribute('name', 'separator');

                separator.append(slotSeparator);
            } else {
                separator.innerHTML = `<wje-icon name=${this.separator || 'chevron-right'}></wje-icon>`;
            }

            fragment.append(separator);
        }

        return fragment;
    }

    /**
     * Returns whether the separator should be visible after this breadcrumb.
     * @returns {boolean}
     */
    shouldRenderSeparator() {
        return this.showSeparator && this.hasVisibleBreadcrumbAfter();
    }

    /**
     * Returns whether any later breadcrumb is still visible in the rendered trail.
     * @returns {boolean}
     */
    hasVisibleBreadcrumbAfter() {
        const breadcrumbs = this.parentElement?.getBreadcrumbs?.();

        if (!Array.isArray(breadcrumbs)) return true;

        const index = breadcrumbs.indexOf(this);

        if (index === -1) return true;

        return breadcrumbs.slice(index + 1).some((breadcrumb) => this.isBreadcrumbVisibleInTrail(breadcrumb));
    }

    /**
     * Returns whether a breadcrumb is visible in the rendered trail.
     * @param {Element} breadcrumb Breadcrumb element to inspect.
     * @returns {boolean}
     */
    isBreadcrumbVisibleInTrail(breadcrumb) {
        return !(breadcrumb?.hasAttribute?.('collapsed') && !breadcrumb?.hasAttribute?.('show-collapsed-indicator'));
    }

    /**
     * Synchronizes host attributes to the internal anchor.
     */
    syncNativeAttributes() {
        const native = this.native || this.context?.querySelector('a.native-breadcrumb');
        if (!native) return;

        const isDisabled = this.disabled;
        const href = this.href;

        if (href !== null && href !== undefined && !isDisabled) {
            native.setAttribute('href', href);
        } else {
            native.removeAttribute('href');
        }

        ['target', 'rel', 'download'].forEach((attr) => {
            if (this.hasAttribute(attr) && !isDisabled) {
                native.setAttribute(attr, this.getAttribute(attr));
            } else {
                native.removeAttribute(attr);
            }
        });

        ['title', 'aria-label'].forEach((attr) => {
            if (this.hasAttribute(attr)) {
                native.setAttribute(attr, this.getAttribute(attr));
            } else {
                native.removeAttribute(attr);
            }
        });

        native.classList.toggle('active', Boolean(this.active));
        native.classList.toggle('disabled', isDisabled);

        if (this.active) native.setAttribute('aria-current', 'page');
        else native.removeAttribute('aria-current');

        if (isDisabled) {
            native.setAttribute('aria-disabled', 'true');
            native.setAttribute('tabindex', '-1');
        } else {
            native.removeAttribute('aria-disabled');
            native.removeAttribute('tabindex');
        }
    }

    /**
     * Sets up native anchor listeners after render.
     */
    afterDraw() {
        this.native?.addEventListener('click', this.handleNativeClick);
    }

    /**
     * Removes native anchor listeners before redraw/disconnect.
     */
    beforeDisconnect() {
        this.native?.removeEventListener('click', this.handleNativeClick);
    }

    /**
     * Prevents disabled breadcrumbs from navigating or bubbling click handlers.
     * @param {MouseEvent} e Click event.
     */
    handleDisabledNativeClick(e) {
        if (!this.disabled) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();
    }

    /**
     * Renders the collapsed indicator based on the current collapsed variant.
     * If the collapsed variant is 'DROPDOWN', it invokes the collapseDropdown method.
     * Mobile breakpoint variants render compact breadcrumb summaries, otherwise the default button is used.
     * @returns {any} The rendered collapsed indicator, either as a dropdown or a button.
     */
    drawCollapsedIndicator() {
        const variant = this.collapsedVariant;

        if (variant === 'DROPDOWN') {
            return this.collapseDropdown();
        }

        if (this.isMobileCollapsedVariant() && this.isBreakpointMenuIndicator()) {
            return this.collapseMobileVariant(variant);
        }

        return this.collapseButton();
    }

    /**
     * Returns whether the current collapsed variant is a compact mobile layout.
     * @returns {boolean}
     */
    isMobileCollapsedVariant() {
        return MOBILE_COLLAPSED_VARIANTS.has(this.collapsedVariant);
    }

    /**
     * Creates and returns a dropdown UI component for collapsed breadcrumbs.
     * This method generates a dropdown element with a button trigger and a menu populated with items corresponding
     * to the collapsed breadcrumbs. The dropdown is configured to handle specific interactions and ensure that
     * events are appropriately managed to avoid propagation issues. Menu items are linked to their corresponding
     * breadcrumbs, enabling the same functionality as clicking on the original breadcrumb.
     * @returns {HTMLElement} A configured dropdown element containing a button as trigger and a menu with breadcrumb items.
     */
    collapseDropdown() {
        const isBreakpointMenuIndicator = this.isBreakpointMenuIndicator();
        const breadcrumbs = this.getCollapsedMenuBreadcrumbs(isBreakpointMenuIndicator);
        const trigger = this.createCollapsedDropdownTrigger(isBreakpointMenuIndicator);

        return this.createBreadcrumbDropdown({
            breadcrumbs,
            trigger,
            placement: isBreakpointMenuIndicator ? 'bottom-start' : 'bottom',
        });
    }

    /**
     * Creates a dropdown containing breadcrumb menu items.
     * @param {object} options Configuration for the dropdown shell.
     * @param {Array<HTMLElement>} options.breadcrumbs Breadcrumb elements to mirror in the menu.
     * @param {HTMLElement} options.trigger Element assigned to the dropdown trigger slot.
     * @param {string} options.placement Floating UI placement token for the popup.
     * @returns {HTMLElement}
     */
    createBreadcrumbDropdown({ breadcrumbs, trigger, placement = 'bottom' }) {
        const dropdown = document.createElement('wje-dropdown');
        dropdown.setAttribute('placement', placement);
        dropdown.setAttribute('offset', '10');

        const menu = this.createCollapsedBreadcrumbMenu(breadcrumbs);

        dropdown.append(trigger, menu);

        return dropdown;
    }

    /**
     * Creates the menu used by collapsed dropdown variants.
     * @param {Array<HTMLElement>} breadcrumbs Breadcrumb items represented by menu items.
     * @returns {HTMLElement}
     */
    createCollapsedBreadcrumbMenu(breadcrumbs) {
        const menu = document.createElement('wje-menu');
        menu.setAttribute('variant', 'context');
        menu.style.setProperty('--wje-menu-item-justify-content', 'flex-start');

        menu.addEventListener('click', (e) => {
            e.stopPropagation();
            e.stopImmediatePropagation?.();
        });

        breadcrumbs.forEach((breadcrumb) => {
            const menuItem = document.createElement('wje-menu-item');
            this.populateCollapsedMenuItem(menuItem, breadcrumb);

            menuItem.__breadcrumb = breadcrumb;

            menuItem.addEventListener('wje-menu-item:click', (e) => {
                e.preventDefault?.();
                e.stopPropagation();
                e.stopImmediatePropagation?.();

                this.forwardBreadcrumbClick(e.currentTarget.__breadcrumb);
            });

            menu.append(menuItem);
        });

        return menu;
    }

    /**
     * Returns breadcrumbs that should be exposed in a collapsed menu.
     * @param {boolean} isBreakpointMenuIndicator Whether this trigger controls the full breakpoint menu.
     * @returns {Array<HTMLElement>}
     */
    getCollapsedMenuBreadcrumbs(isBreakpointMenuIndicator) {
        const breadcrumbs = this.getBreadcrumbs();

        if (isBreakpointMenuIndicator) {
            return breadcrumbs?.getBreadcrumbs?.() || [];
        }

        return breadcrumbs?.getBreadcrumbsCollapsed?.() || [];
    }

    /**
     * Renders one of the compact mobile breakpoint variants.
     * @param {string} variant Normalized uppercase variant token.
     * @returns {HTMLElement}
     */
    collapseMobileVariant(variant) {
        if (variant === 'TEXT') return this.collapseMobileText();
        if (variant === 'BACK') return this.collapseMobileBack();
        if (variant === 'PARENT-TITLE') return this.collapseMobileParentTitle();
        if (variant === 'MENU-TITLE') return this.collapseMobileMenuTitle();
        if (variant === 'SHEET') return this.collapseMobileSheet();

        return this.collapseButton();
    }

    /**
     * Renders only the current breadcrumb title.
     * @returns {HTMLElement}
     */
    collapseMobileText() {
        const current = this.getCurrentBreadcrumb();
        const container = this.createMobileContainer('breadcrumb-mobile--text');
        const title = this.createMobileBreadcrumbControl(current, 'breadcrumb-mobile__title', {
            ariaCurrent: true,
            actionable: this.isBreadcrumbActionable(current),
        });

        container.append(title);

        return container;
    }

    /**
     * Renders a parent back action above the current title.
     * @returns {HTMLElement}
     */
    collapseMobileBack() {
        const current = this.getCurrentBreadcrumb();
        const parent = this.getParentBreadcrumb();
        const container = this.createMobileContainer('breadcrumb-mobile--back');

        if (parent) {
            const back = this.createMobileBreadcrumbControl(parent, 'breadcrumb-mobile__back', {
                ariaLabel: 'Go to parent',
                icon: 'arrow-left',
                actionable: this.isBreadcrumbActionable(parent),
            });
            container.append(back);
        }

        const title = this.createMobileBreadcrumbControl(current, 'breadcrumb-mobile__title', {
            ariaCurrent: true,
            actionable: false,
        });
        container.append(title);

        return container;
    }

    /**
     * Renders parent context above the current title.
     * @returns {HTMLElement}
     */
    collapseMobileParentTitle() {
        const current = this.getCurrentBreadcrumb();
        const parent = this.getParentBreadcrumb();
        const container = this.createMobileContainer('breadcrumb-mobile--parent-title');

        if (parent) {
            const parentControl = this.createMobileBreadcrumbControl(parent, 'breadcrumb-mobile__parent', {
                actionable: this.isBreadcrumbActionable(parent),
            });
            container.append(parentControl);
        }

        const title = this.createMobileBreadcrumbControl(current, 'breadcrumb-mobile__title', {
            ariaCurrent: true,
            actionable: false,
        });
        container.append(title);

        return container;
    }

    /**
     * Renders a menu trigger with the current title.
     * @returns {HTMLElement}
     */
    collapseMobileMenuTitle() {
        const trigger = this.createMobileDropdownTrigger({
            icon: 'dots',
            label: this.getBreadcrumbLabel(this.getCurrentBreadcrumb()),
            ariaLabel: 'Show breadcrumb menu',
            className: 'breadcrumb-mobile--menu-title',
        });

        return this.createMobileDropdown(trigger);
    }

    /**
     * Renders the future sheet variant using the dropdown fallback for now.
     * @returns {HTMLElement}
     */
    collapseMobileSheet() {
        const trigger = this.createMobileDropdownTrigger({
            icon: 'chevron-down',
            iconPosition: 'end',
            label: this.getBreadcrumbLabel(this.getCurrentBreadcrumb()),
            ariaLabel: 'Show breadcrumb path',
            className: 'breadcrumb-mobile--sheet',
        });

        return this.createMobileDropdown(trigger);
    }

    /**
     * Creates a dropdown fallback for mobile menu-like variants.
     * @param {HTMLElement} trigger Element that opens the fallback dropdown.
     * @returns {HTMLElement}
     */
    createMobileDropdown(trigger) {
        const dropdown = this.createBreadcrumbDropdown({
            breadcrumbs: this.getTrailBreadcrumbs(),
            trigger,
            placement: 'bottom-start',
        });

        dropdown.classList.add('breadcrumb-mobile-dropdown');

        return dropdown;
    }

    /**
     * Creates a compact mobile dropdown trigger.
     * @param {object} options Visual settings for the compact trigger.
     * @param {string} options.icon Icon shown next to the title.
     * @param {string} options.iconPosition Whether the icon renders at the start or end.
     * @param {string} options.label Current breadcrumb label shown beside the icon.
     * @param {string} options.ariaLabel Accessible name for the dropdown trigger.
     * @param {string} options.className Variant-specific class applied to the trigger.
     * @returns {HTMLElement}
     */
    createMobileDropdownTrigger({ icon, iconPosition = 'start', label, ariaLabel, className }) {
        const trigger = document.createElement('wje-button');
        trigger.setAttribute('slot', 'trigger');
        trigger.setAttribute('fill', 'link');
        trigger.setAttribute('label', ariaLabel);
        trigger.classList.add('breadcrumb-mobile', 'breadcrumb-mobile__menu', className);

        const iconElement = document.createElement('wje-icon');
        iconElement.setAttribute('name', icon);
        iconElement.setAttribute('slot', iconPosition === 'end' ? 'end' : 'start');
        iconElement.classList.add('breadcrumb-mobile__icon');

        const title = document.createElement('span');
        title.classList.add('breadcrumb-mobile__title');
        title.setAttribute('aria-current', 'page');
        title.textContent = label;
        if (label) title.setAttribute('title', label);

        if (iconPosition === 'end') {
            trigger.append(title, iconElement);
        } else {
            trigger.append(iconElement, title);
        }

        return trigger;
    }

    /**
     * Creates a wrapper for compact mobile breadcrumb variants.
     * @param {string} className Variant class.
     * @returns {HTMLElement}
     */
    createMobileContainer(className) {
        const container = document.createElement('div');
        container.classList.add('breadcrumb-mobile', className);

        return container;
    }

    /**
     * Creates a text or button control for a breadcrumb.
     * @param {HTMLElement|null} breadcrumb Source breadcrumb.
     * @param {string} className Class applied to the created control.
     * @param {object} options Behavior and accessibility settings for the control.
     * @param {boolean} options.actionable Whether clicks should forward to the source breadcrumb.
     * @param {boolean} options.ariaCurrent Whether the control marks the current page.
     * @param {string} options.ariaLabel Accessible name for action controls.
     * @param {string} options.icon Optional icon shown before the label.
     * @returns {HTMLElement}
     */
    createMobileBreadcrumbControl(breadcrumb, className, { actionable = false, ariaCurrent = false, ariaLabel, icon } = {}) {
        const label = this.getBreadcrumbLabel(breadcrumb);
        const control = document.createElement(actionable ? 'button' : 'span');

        control.classList.add(className);
        if (actionable) {
            control.setAttribute('type', 'button');
            control.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation?.();
                this.forwardBreadcrumbClick(breadcrumb);
            });
        }

        if (ariaCurrent) control.setAttribute('aria-current', 'page');
        if (ariaLabel && actionable) control.setAttribute('aria-label', ariaLabel);
        if (label) control.setAttribute('title', label);

        if (icon) {
            const iconElement = document.createElement('wje-icon');
            iconElement.setAttribute('name', icon);
            iconElement.classList.add('breadcrumb-mobile__icon');
            control.append(iconElement);
        }

        const text = document.createElement('span');
        text.classList.add('breadcrumb-mobile__label');
        text.textContent = label;
        control.append(text);

        return control;
    }

    /**
     * Returns the full trail of sibling breadcrumbs.
     * @returns {Array<HTMLElement>}
     */
    getTrailBreadcrumbs() {
        return this.getBreadcrumbs()?.getBreadcrumbs?.() || [];
    }

    /**
     * Returns the current breadcrumb item.
     * @returns {HTMLElement|null}
     */
    getCurrentBreadcrumb() {
        const breadcrumbs = this.getTrailBreadcrumbs();

        return breadcrumbs[breadcrumbs.length - 1] || null;
    }

    /**
     * Returns the parent breadcrumb item.
     * @returns {HTMLElement|null}
     */
    getParentBreadcrumb() {
        const breadcrumbs = this.getTrailBreadcrumbs();

        return breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 2] : null;
    }

    /**
     * Returns whether a breadcrumb has a known action to forward.
     * @param {HTMLElement|null} breadcrumb Breadcrumb to inspect.
     * @returns {boolean}
     */
    isBreadcrumbActionable(breadcrumb) {
        if (!breadcrumb || breadcrumb.disabled || breadcrumb.hasAttribute?.('disabled')) return false;

        return Boolean(
            breadcrumb.hasAttribute?.('href') ||
            breadcrumb.hasAttribute?.('onclick') ||
            breadcrumb.__wjeBreadcrumbManaged ||
            breadcrumb.hasAttribute?.('data-wje-breadcrumbs-item')
        );
    }

    /**
     * Forwards a compact control or menu item click to the original breadcrumb.
     * @param {HTMLElement|null} breadcrumb Source breadcrumb.
     */
    forwardBreadcrumbClick(breadcrumb) {
        if (!breadcrumb) return;

        const native = breadcrumb.native || breadcrumb.context?.querySelector('a.native-breadcrumb');
        if (native && typeof native.click === 'function') {
            native.click();
        } else if (typeof breadcrumb.click === 'function') {
            breadcrumb.click();
        } else {
            breadcrumb.dispatchEvent(
                new MouseEvent('click', {
                    bubbles: true,
                    composed: true,
                    cancelable: true
                })
            );
        }
    }

    /**
     * Creates the dropdown trigger for collapsed breadcrumbs.
     * @param {boolean} isBreakpointMenuIndicator Whether this trigger controls the full breakpoint menu.
     * @returns {HTMLElement}
     */
    createCollapsedDropdownTrigger(isBreakpointMenuIndicator) {
        if (isBreakpointMenuIndicator) {
            const customTrigger = this.getBreadcrumbs()?.getBreakpointCollapseTrigger?.();

            if (customTrigger) {
                const trigger = customTrigger.cloneNode(true);
                trigger.setAttribute('slot', 'trigger');

                if (trigger.localName === 'wje-button') {
                    if (!trigger.hasAttribute('label') && !trigger.hasAttribute('aria-label')) {
                        trigger.setAttribute('label', 'Show breadcrumb menu');
                    }
                } else if (!trigger.hasAttribute('aria-label')) {
                    trigger.setAttribute('aria-label', 'Show breadcrumb menu');
                }

                return trigger;
            }
        }

        let button = document.createElement('wje-button');
        button.setAttribute('slot', 'trigger');
        button.setAttribute('fill', 'link');
        button.setAttribute('label', 'Show more breadcrumbs');
        button.innerHTML = `<wje-icon name="${this.getCollapsedDropdownIcon(isBreakpointMenuIndicator)}"></wje-icon>`;

        return button;
    }

    /**
     * Resolves the icon used by the default dropdown trigger.
     * @param {boolean} isBreakpointMenuIndicator Whether this trigger controls the full breakpoint menu.
     * @returns {string}
     */
    getCollapsedDropdownIcon(isBreakpointMenuIndicator) {
        if (!isBreakpointMenuIndicator) return 'dots';

        return this.getBreadcrumbs()?.breakpointCollapseIcon || 'menu';
    }

    /**
     * Copies breadcrumb content into a collapsed menu item.
     * @param {HTMLElement} menuItem Menu item receiving the content.
     * @param {HTMLElement} breadcrumb Source breadcrumb.
     */
    populateCollapsedMenuItem(menuItem, breadcrumb) {
        const nodes = Array.from(breadcrumb.childNodes).map((node) => node.cloneNode(true));

        menuItem.replaceChildren(...nodes);

        if (menuItem.textContent.trim()) return;

        const label = this.getBreadcrumbLabel(breadcrumb);

        if (label) {
            menuItem.append(document.createTextNode(label));
        }
    }

    /**
     * Resolves a readable label for a breadcrumb item.
     * @param {HTMLElement|null} breadcrumb Source breadcrumb.
     * @returns {string}
     */
    getBreadcrumbLabel(breadcrumb) {
        if (!breadcrumb) return '';

        return this.getCollapsedMenuItemLabel(breadcrumb);
    }

    /**
     * Resolves a readable label for icon-only collapsed menu items.
     * @param {HTMLElement} breadcrumb Source breadcrumb.
     * @returns {string}
     */
    getCollapsedMenuItemLabel(breadcrumb) {
        const explicitLabel = breadcrumb.getAttribute('aria-label') || breadcrumb.getAttribute('title');

        if (explicitLabel?.trim()) return explicitLabel.trim();

        const text = breadcrumb.textContent.trim();

        if (text) return text;

        const iconName = breadcrumb.querySelector('wje-icon[slot="start"]')?.getAttribute('name');

        if (iconName === 'home') return 'Home';

        const href = breadcrumb.getAttribute('href');
        const path = href?.split(/[?#]/)[0] || '';
        const segment = path.split('/').filter(Boolean).pop();

        return segment ? this.humanizeCollapsedMenuItemLabel(segment) : '';
    }

    /**
     * Turns a URL segment into a readable menu label.
     * @param {string} value URL segment.
     * @returns {string}
     */
    humanizeCollapsedMenuItemLabel(value) {
        return decodeURIComponent(value)
            .replace(/[-_]+/g, ' ')
            .replace(/\b\w/g, (letter) => letter.toUpperCase());
    }

    /**
     * Returns whether this collapsed indicator represents the full breakpoint menu.
     * @returns {boolean}
     */
    isBreakpointMenuIndicator() {
        const breadcrumbs = this.parentElement;

        return (
            this.hasAttribute('show-collapsed-indicator') &&
            this.hasAttribute('collapsed') &&
            breadcrumbs?.isBreakpointMenuCollapseActive?.()
        );
    }

    /**
     * Creates a button element that expands hidden breadcrumbs when clicked.
     * The button is set with appropriate attributes and event listeners to handle
     * the expanding of hidden breadcrumb elements. Clicking the button will remove
     * the button itself, reveal hidden breadcrumbs, and stop the current event
     * propagation.
     * @returns {HTMLButtonElement} The created button configured to expand breadcrumbs.
     */
    collapseButton() {
        let button = document.createElement('button');
        button.setAttribute('aria-label', 'Show more breadcrumbs');
        button.setAttribute('part', 'collapsed-indicator');
        button.innerHTML = `<wje-icon name="dots"></wje-icon>`;

        event.addListener(button, 'click', null, (e) => {
            this.native.classList.remove('hidden');
            button.remove();
            this.getBreadcrumbs().getBreadcrumbsCollapsed().forEach((el) => {
                el.classList.remove('collapsed');
            });
            e.stopPropagation();
        });

        return button;
    }

    /**
     * Retrieves the breadcrumb trail for the current element by returning its parent element.
     * @returns {Element} The parent element representing the breadcrumb trail.
     */
    getBreadcrumbs() {
        return this.parentElement;
    }
}
